import { Head, router } from "@inertiajs/react"
import { Gift, Package, Plus, Sparkles } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { RewardForm } from "@/components/rewards/reward-form"
import type { RewardFormReward } from "@/components/rewards/reward-form"
import { RewardRowActions } from "@/components/rewards/reward-row-actions"
import {
  type RewardState,
  RewardStateSelect,
} from "@/components/rewards/reward-state-select"
import { RewardsTabStrip } from "@/components/rewards-redemptions/rewards-tab-strip"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { finalFocusByTestId } from "@/lib/final-focus"
import { newRewardPath, rewardPath, rewardsPath } from "@/routes"

/**
 * OKS-59 — Brand Rewards index page.
 *
 * Hosts the catalogue list (`Table` ≥ md, card grid < md) AND the
 * New/Edit `Sheet` driven by `?new=1` / `?edit=:id` URL state. URL
 * state means the Sheet open/close survives refresh, share, and
 * back-button navigation (TRD K2).
 *
 * UX states covered (UX Spec §4):
 *   - Empty   — full-bleed `Card` with Gift icon, headline, body, CTA.
 *   - Loading — Inertia renders SSR-first; partial reloads on toggle
 *               / delete keep the table mounted (`preserveState`).
 *   - Error   — toggle/delete failures revert the optimistic flip and
 *               surface via `sonner` toast from the controller flash.
 *   - Success — Sheet closes on server redirect; toast fires from
 *               flash; row appears at the top of the list (default
 *               `created_at: :desc` sort).
 *   - Edge    — long titles clamp to 1 line in Table / 2 lines in
 *               cards (`title=` attr surfaces full); zero-cost shows
 *               "0 coins"; all-inactive lists still render (dimmed).
 */
export interface RewardRow {
  id: number
  title: string
  description: string
  kind: "product" | "experience" | "custom"
  coin_cost: number
  /**
   * OKS-62 — per-reward inventory ceiling. `null` ⇒ unlimited;
   * `0` ⇒ depleted (destructive Badge in the index); `N > 0` ⇒ "N left".
   * See {@link StockCell} for the three-mode rendering.
   */
  stock: number | null
  // OKS-407 — 3-state enum replaces the `is_active` boolean (TRD KD-2).
  state: RewardState
  // OKS-438 — resolved cover URL (attached `:thumb` variant → legacy
  // string → null) from `cover_image_display_url` (TRD KD-2). Drives the
  // leading table/card thumbnail + (on edit) the Sheet preview.
  cover_image_url: string | null
  // OKS-438 — true when an Active Storage attachment exists; threaded
  // into the edit Sheet to gate the Remove affordance.
  has_cover_image: boolean
  created_at: string
}

interface OpenFormPropsNew {
  mode: "new"
}

interface OpenFormPropsEdit {
  mode: "edit"
  reward: RewardRow
}

type OpenFormProps = OpenFormPropsNew | OpenFormPropsEdit

interface RewardsIndexProps {
  rewards: RewardRow[]
  is_empty: boolean
  open_form: OpenFormProps | null
  kinds: string[]
  // OKS-61 — Drives the pending-count badge on the new
  // `<RewardsTabStrip>` Redemptions tab. Server-rendered single COUNT
  // over the policy-scoped Redemption relation (no N+1, no caching).
  //
  // Optional in the prop interface so existing OKS-59 tests don't
  // need to thread a 0 through every render call site; the controller
  // always emits a real Integer. The component defaults to 0 (which
  // hides the badge per UX-OQ-3 positive-space empty signal).
  pending_redemptions_count?: number
}

const KIND_ICON = {
  product: Package,
  experience: Sparkles,
  custom: Gift,
} as const

const KIND_BADGE_VARIANT = {
  product: "default",
  experience: "secondary",
  custom: "outline",
} as const

export default function RewardsIndex({
  rewards,
  is_empty,
  open_form,
  kinds,
  pending_redemptions_count = 0,
}: RewardsIndexProps) {
  const { t } = useTranslation()
  const sheetOpen = open_form !== null

  // OKS-780 — reconcile the stranded `new`/`edit` marker against the LIVE
  // address on every Inertia visit completion.
  //
  // The shared `recent_notifications` deferred prop (application_controller.rb,
  // gated `if Current.user && Current.brand`) is born on mount carrying the
  // current `?new=1` / `?edit=:id` marker. If the admin dismisses the Sheet
  // before that fetch lands, the deferred reload applies LAST and pushes its
  // birth URL back: the Sheet stays closed (`open_form` is omitted from the
  // deferred's `only` partial, so it holds the `null` `closeSheet()` set) but
  // the address re-strands the marker — a refresh then reads it server-side and
  // reopens the dismissed editor. OKS-746 mechanism; framework fix = OKS-788.
  //
  // Why this shape (and not the render-keyed effect that shipped first):
  //   - The deferred re-strands `window.location` via Inertia's own history
  //     write. Keying an effect on `usePage().url` is BLIND to that write once
  //     the prop-reflected URL and the live address diverge (the OKS-744
  //     two-URL-truths trap), so the effect never re-fires and the strand
  //     stands. We therefore read `window.location.search` LIVE at reconcile
  //     time, never a prop snapshot.
  //   - We reconcile on the `finish`/`navigate` events, which fire AFTER a
  //     visit's history write commits (the deferred reload emits them via
  //     `loadDeferredProps → doReload`). Firing after the deferred's own write
  //     means there is no later queued write for our `router.replace` to lose
  //     to — the failure mode of the render-keyed attempt.
  //
  // `router.replace` is a client-side visit (no server round-trip; keeps
  // Inertia's page URL + history entry in sync). It re-fires the events, but
  // the marker is gone by then so the reconcile is a no-op — no loop. A
  // legitimate open (`open_form !== null`) is always a no-op via the ref guard,
  // so the editor can never be force-closed.
  const openFormRef = useRef(open_form)
  useEffect(() => {
    openFormRef.current = open_form
  }, [open_form])
  useEffect(() => {
    const reconcile = () => {
      if (openFormRef.current !== null) return
      if (!urlHasFormMarker(window.location.search)) return
      router.replace({
        url: rewardsPath(),
        preserveScroll: true,
        preserveState: true,
      })
    }
    // `finish` fires after every visit's history write commits (incl. the
    // deferred reload, verified against @inertiajs/core@2.3.14 + measured:
    // the `navigate`/`finish` handler observes `window.location` already
    // carrying the re-stranded `?new=1`, so there is no later queued write to
    // lose to — the failure mode of the render-keyed attempt). `navigate` is
    // kept as a belt-and-suspenders sibling for same-document history updates.
    const stopFinish = router.on("finish", reconcile)
    const stopNavigate = router.on("navigate", reconcile)
    // Catch a strand already present when the effect mounts.
    reconcile()
    return () => {
      stopFinish()
      stopNavigate()
    }
  }, [])

  return (
    <AppLayout>
      <Head title={t("rewards.head_title")} />
      <div className="flex flex-col gap-6 p-6">
        {/* OKS-61 — tab strip companion to the catalogue. NO new
            sidebar slot (UX-OQ-1); discoverability for the Redemptions
            queue is via this tab strip. Active tab = "catalogue" on
            this page. */}
        <RewardsTabStrip
          activeTab="catalogue"
          pendingCount={pending_redemptions_count}
        />
        <header className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <h1 className="text-foreground text-2xl font-semibold tracking-tight">
              {t("rewards.index.title")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("rewards.index.subtitle")}
            </p>
          </div>
          <Button
            onClick={() => openSheet("new")}
            data-testid="rewards-new-cta"
          >
            <Plus className="mr-1 size-4" aria-hidden="true" />
            {t("rewards.index.new_cta")}
          </Button>
        </header>

        {is_empty ? (
          <RewardsEmptyState />
        ) : (
          <>
            <RewardsTable rewards={rewards} />
            <RewardsCardGrid rewards={rewards} />
          </>
        )}
      </div>

      <RewardSheet open={sheetOpen} openForm={open_form} kinds={kinds} />
    </AppLayout>
  )
}

/**
 * OKS-780 — true when `url` carries a reward-editor marker that would reopen
 * the Sheet on a fresh server read (`?new=<truthy>` or `?edit=<truthy>`).
 *
 * Mirrors the controller's `params[:new].present?` / `params[:edit].present?`
 * semantics (`open_form_props`, rewards_controller.rb): a param that is absent,
 * or present-but-blank, does NOT open the Sheet, so it is not a strand and is
 * left untouched. `url` is an Inertia relative URL (e.g. `/brand/rewards?new=1`).
 */
export function urlHasFormMarker(url: string): boolean {
  const queryStart = url.indexOf("?")
  if (queryStart === -1) return false
  const params = new URLSearchParams(url.slice(queryStart))
  const present = (value: string | null) =>
    value !== null && value.trim() !== ""
  return present(params.get("new")) || present(params.get("edit"))
}

function openSheet(modeOrId: "new" | number) {
  if (modeOrId === "new") {
    router.get(
      rewardsPath(),
      { new: "1" },
      { preserveScroll: true, preserveState: true, only: ["open_form"] },
    )
  } else {
    router.get(
      rewardsPath(),
      { edit: modeOrId },
      { preserveScroll: true, preserveState: true, only: ["open_form"] },
    )
  }
}

function closeSheet() {
  router.get(
    rewardsPath(),
    {},
    { preserveScroll: true, preserveState: true, only: ["open_form"] },
  )
}

function RewardsEmptyState() {
  const { t } = useTranslation()

  return (
    <Card className="border-dashed" data-testid="rewards-empty-state">
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <Gift className="text-muted-foreground size-12" aria-hidden="true" />
        <div className="space-y-2">
          <h2 className="text-lg font-medium">{t("rewards.empty.title")}</h2>
          <p className="text-muted-foreground max-w-md text-sm">
            {t("rewards.empty.body")}
          </p>
        </div>
        <Button
          onClick={() => openSheet("new")}
          data-testid="rewards-empty-cta"
        >
          <Plus className="mr-1 size-4" aria-hidden="true" />
          {t("rewards.empty.cta")}
        </Button>
      </CardContent>
    </Card>
  )
}

function RewardsTable({ rewards }: { rewards: RewardRow[] }) {
  const { t } = useTranslation()

  return (
    <div className="hidden md:block">
      <Table data-testid="rewards-table">
        <TableHeader>
          <TableRow>
            <TableHead className="hidden w-[64px] sm:table-cell">
              <span className="sr-only">{t("rewards.table.cover")}</span>
            </TableHead>
            <TableHead className="w-[140px]">
              {t("rewards.table.kind")}
            </TableHead>
            <TableHead>{t("rewards.table.title")}</TableHead>
            <TableHead className="w-[140px]">
              {t("rewards.table.coin_cost")}
            </TableHead>
            <TableHead scope="col" className="w-[140px]">
              {t("rewards.table.stock")}
            </TableHead>
            <TableHead className="w-[160px]">
              {t("rewards.table.state")}
            </TableHead>
            <TableHead className="w-[80px] text-right">
              <span className="sr-only">{t("rewards.table.actions")}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rewards.map((reward) => (
            <RewardTableRow key={reward.id} reward={reward} />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function RewardTableRow({ reward }: { reward: RewardRow }) {
  const { t } = useTranslation()
  const Icon = KIND_ICON[reward.kind]

  return (
    <TableRow
      data-testid="reward-table-row"
      data-reward-id={reward.id}
      className={reward.state === "available" ? undefined : "opacity-60"}
    >
      <TableCell className="hidden sm:table-cell">
        <RewardThumbnail coverUrl={reward.cover_image_url} kind={reward.kind} />
      </TableCell>
      <TableCell>
        <Badge
          variant={KIND_BADGE_VARIANT[reward.kind]}
          className="inline-flex items-center gap-1.5"
        >
          <Icon className="size-3" aria-hidden="true" />
          {t(`rewards.kind.${reward.kind}`)}
        </Badge>
      </TableCell>
      <TableCell>
        <span className="line-clamp-1" title={reward.title}>
          {reward.title}
        </span>
      </TableCell>
      <TableCell className="tabular-nums">
        {reward.coin_cost} {t("rewards.table.coin_unit")}
      </TableCell>
      <TableCell data-testid="reward-stock-cell">
        <StockCell stock={reward.stock} />
      </TableCell>
      <TableCell>
        <RewardStateSelect
          rewardId={reward.id}
          rewardTitle={reward.title}
          state={reward.state}
        />
      </TableCell>
      <TableCell className="text-right">
        <RewardRowActions
          rewardId={reward.id}
          rewardTitle={reward.title}
          onEdit={(id) => {
            openSheet(id)
          }}
        />
      </TableCell>
    </TableRow>
  )
}

function RewardsCardGrid({ rewards }: { rewards: RewardRow[] }) {
  const { t } = useTranslation()

  return (
    <div className="grid gap-3 md:hidden" data-testid="rewards-card-grid">
      {rewards.map((reward) => {
        const Icon = KIND_ICON[reward.kind]
        return (
          <Card
            key={reward.id}
            data-testid="reward-card"
            data-reward-id={reward.id}
            className={reward.state === "available" ? undefined : "opacity-60"}
          >
            <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
              <div className="flex items-center gap-3">
                <RewardThumbnail
                  coverUrl={reward.cover_image_url}
                  kind={reward.kind}
                />
                <Badge
                  variant={KIND_BADGE_VARIANT[reward.kind]}
                  className="inline-flex items-center gap-1.5"
                >
                  <Icon className="size-3" aria-hidden="true" />
                  {t(`rewards.kind.${reward.kind}`)}
                </Badge>
              </div>
              <RewardRowActions
                rewardId={reward.id}
                rewardTitle={reward.title}
                onEdit={(id) => {
                  openSheet(id)
                }}
              />
            </CardHeader>
            <CardContent className="space-y-2">
              <CardTitle
                className="line-clamp-2 text-base font-medium"
                title={reward.title}
              >
                {reward.title}
              </CardTitle>
              <p className="text-muted-foreground line-clamp-2 text-sm">
                {reward.description}
              </p>
              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="tabular-nums">
                    {reward.coin_cost} {t("rewards.table.coin_unit")}
                  </span>
                  <span aria-hidden="true" className="text-muted-foreground">
                    ·
                  </span>
                  <span data-testid="reward-card-stock">
                    <StockCell stock={reward.stock} />
                  </span>
                </div>
                <RewardStateSelect
                  rewardId={reward.id}
                  rewardTitle={reward.title}
                  state={reward.state}
                />
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

/**
 * OKS-62 — Three-mode stock cell. Used by both the ≥md Table column
 * and the <md Card meta line (UX Spec §2).
 *
 * Mode selection (UX Spec §4 + PRD OQ-3):
 *
 *   stock === null  → "Unlimited" (muted) — unambiguous to the brand
 *                     admin reading the row; never rendered as "—".
 *   stock <=  0     → destructive Badge "Out of stock" — the badge
 *                     carries the depleted signal in isolation; the
 *                     containing row does NOT dim (UX Spec §4 / OQ-4
 *                     — dim is reserved for `is_active=false`).
 *   stock >   0     → pluralised "N left" via i18n's `{count, plural}`
 *                     rule. Singular `1 left` / plural `N left` (EN);
 *                     `1 restant` / `N restants` (FR).
 *
 * Why the badge variant matches no other UX in this page:
 *   - "Out of stock" is a destructive operational state (brand cannot
 *     fulfil claims). Destructive variant aligns with the same colour
 *     family the Delete button uses, so the visual weight signals
 *     "needs attention" — not "permanently broken".
 *
 * The cell is intentionally inline-render-friendly (no fixed width)
 * so the same component slots into a TableCell and a flex-wrap meta
 * line without layout-arg gymnastics.
 */
function StockCell({ stock }: { stock: number | null }) {
  const { t } = useTranslation()

  if (stock === null) {
    return (
      <span
        className="text-muted-foreground"
        data-testid="reward-stock-unlimited"
      >
        {t("rewards.index.stock_unlimited")}
      </span>
    )
  }

  if (stock <= 0) {
    return (
      <Badge variant="destructive" data-testid="reward-stock-out">
        {t("rewards.index.stock_out")}
      </Badge>
    )
  }

  return (
    <span className="tabular-nums" data-testid="reward-stock-remaining">
      {t("rewards.index.stock_remaining", { count: stock })}
    </span>
  )
}

/**
 * OKS-438 — Leading cover thumbnail for the brand catalogue table row
 * and card-grid header (~48px square, rounded). Renders the resolved
 * cover (`:thumb` variant or legacy URL) with an onError fall-through to
 * the kind-icon tile — same vocabulary as the creator `<RewardCover>`.
 * Decorative (`aria-hidden`); the row's accessible name comes from the
 * title cell, not the image.
 */
function RewardThumbnail({
  coverUrl,
  kind,
}: {
  coverUrl: string | null
  kind: RewardRow["kind"]
}) {
  const [errored, setErrored] = useState(false)
  const Icon = KIND_ICON[kind]
  const showImage = Boolean(coverUrl) && !errored

  if (showImage) {
    return (
      <img
        src={coverUrl!}
        alt=""
        aria-hidden="true"
        loading="lazy"
        onError={() => setErrored(true)}
        data-testid="reward-thumbnail"
        className="size-12 shrink-0 rounded-md object-cover"
      />
    )
  }

  return (
    <div
      aria-hidden="true"
      data-testid="reward-thumbnail-fallback"
      className="bg-muted text-muted-foreground flex size-12 shrink-0 items-center justify-center rounded-md"
    >
      <Icon className="size-5" />
    </div>
  )
}

interface RewardSheetProps {
  open: boolean
  openForm: OpenFormProps | null
  kinds: string[]
}

function RewardSheet({ open, openForm, kinds }: RewardSheetProps) {
  const { t } = useTranslation()

  // Note: the brief flicker between `openForm === null` and the Sheet's
  // exit animation completing is acceptable; the Sheet content unmounts
  // on close anyway. Keeping the render derived directly from props
  // avoids a cache-vs-prop sync hazard.
  const formProps = useMemo<{
    reward: RewardFormReward
    title: string
    action: string
    method: "post" | "patch"
  } | null>(() => {
    if (openForm === null) return null

    if (openForm.mode === "new") {
      return {
        reward: {
          id: null,
          title: "",
          description: "",
          kind: null,
          coin_cost: 0,
          // OKS-62 — default new-reward stock is NULL (unlimited). The
          // form's blank Stock input round-trips as "" → controller
          // coerces to nil → DB stores NULL. PRD OQ-2.
          stock: null,
          // OKS-407 — new rewards default to `available`.
          state: "available",
          // OKS-438 — new rewards have no cover attachment.
          cover_image_url: null,
          has_cover_image: false,
        },
        title: t("rewards.form.new_title"),
        action: rewardsPath(),
        method: "post",
      }
    }

    return {
      reward: {
        id: openForm.reward.id,
        title: openForm.reward.title,
        description: openForm.reward.description,
        kind: openForm.reward.kind,
        coin_cost: openForm.reward.coin_cost,
        stock: openForm.reward.stock,
        state: openForm.reward.state,
        cover_image_url: openForm.reward.cover_image_url,
        has_cover_image: openForm.reward.has_cover_image,
      },
      title: t("rewards.form.edit_title"),
      action: rewardPath(openForm.reward.id),
      method: "patch",
    }
  }, [openForm, t])

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) closeSheet()
      }}
    >
      {/*
       * OKS-657 — the RewardSheet captures no `SheetTrigger` (it opens from
       * `?new=1` / `?edit=:id` URL state), so Base UI has no default
       * return-focus target and focus would fall to `<body>` on close. Pin it
       * to the always-mounted header "New reward" CTA. For a Sheet opened from
       * a per-row edit button, close-focus deterministically anchors to the
       * header CTA rather than the (possibly re-rendered) row button — the
       * §9-sanctioned list-header fallback (PRD §7 accepted trade-off).
       */}
      <SheetContent
        side="right"
        className="overflow-y-auto sm:max-w-lg"
        data-testid="reward-sheet"
        finalFocus={finalFocusByTestId("rewards-new-cta")}
      >
        {formProps ? (
          <>
            <SheetHeader>
              <SheetTitle>{formProps.title}</SheetTitle>
              <SheetDescription>
                {t("rewards.form.description")}
              </SheetDescription>
            </SheetHeader>
            <div className="px-4 py-4">
              <RewardForm
                key={
                  formProps.reward.id === null
                    ? "new"
                    : `edit-${String(formProps.reward.id)}`
                }
                reward={formProps.reward}
                kinds={kinds}
                action={formProps.action}
                method={formProps.method}
                onCancel={closeSheet}
              />
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

// Re-export the standalone-page helper so direct route entry points
// can deep-link to /brand/rewards/new without round-tripping through
// the index URL-state Sheet.
export { newRewardPath }
